import type { MultiValue } from '@ksyos/design-system';
import {
    Card,
    Form,
    FormButtonRow,
    IconButton,
    Select,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@ksyos/design-system';
import { useEffect, useState } from 'react';
import type { Departments, ManagerRoleName } from '@common/enums';
import type { IManagerRoleWithAuthorization } from '@common/interfaces';
import type { ManagerAuthorizationsGroupedByRole } from '@common/validation/managerAuthorizations';
import { ManagerAuthorization } from '@common/enums';
import { managerAuthorizationsSchema } from '@common/validation/managerAuthorizations';
import { CancelButton } from '@frontend/components/userDataManagement/CancelButton';
import { SaveButton } from '@frontend/components/userDataManagement/SaveButton';
import { translateKey } from '@frontend/translate';
import { assertHasAuthorization, capitalizeFirstLetter, useAbortableFetch, validate } from '@frontend/utils';
import { managerAuthorizationDescriptionTranslation } from './managerAuthorizationDescriptionTranslation';

interface IManageRolesAuthorizationDetails {
    department: Departments;
}

export function ManagerRolesAuthorizationDetails(props: IManageRolesAuthorizationDetails) {
    const { department } = props;
    const abortableFetch = useAbortableFetch();
    const [managerAuthorizations, setManagerAuthorizations] = useState<ManagerAuthorizationsGroupedByRole>();
    const [isEditing, setIsEditing] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        loadManagerRolesWithAuthorizations();
    }, [department, isEditing]);

    assertHasAuthorization(ManagerAuthorization.managerAuthorizations);

    return (
        <Card>
            <Card.Transition type={isEditing ? 'edit' : 'read'} />
            <Card.Header
                action={
                    !isEditing && (
                        <IconButton
                            icon="edit-light"
                            labelText="edit"
                            onClick={() => setIsEditing(true)}
                        />
                    )
                }
                title={department}
            />
            <Card.Body>
                <Form submitOnEnter={false}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableHeader>{capitalizeFirstLetter(translateKey('common.role'))}</TableHeader>
                                <TableHeader>{translateKey('common.authorizations')}</TableHeader>
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {managerAuthorizations &&
                                Object.keys(managerAuthorizations)
                                    .sort()
                                    .map((role) => {
                                        const managerRoleWithAuthorizations =
                                            managerAuthorizations[role as ManagerRoleName];

                                        return (
                                            <TableRow key={role}>
                                                <TableCell>{role}</TableCell>
                                                <TableCell>
                                                    {isEditing ? (
                                                        <Select
                                                            className="automation-choose-authorization"
                                                            closeMenuOnSelect={false}
                                                            defaultValue={getValues(managerRoleWithAuthorizations)}
                                                            isMulti
                                                            onChange={(item) => handleChange(item, role)}
                                                            options={getOptions()}
                                                            placeholder={translateKey('practitioner.chooseRole')}
                                                        />
                                                    ) : (
                                                        getManagerRoleAuthorizationText(managerRoleWithAuthorizations)
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                        </TableBody>
                    </Table>
                    {isEditing && (
                        <FormButtonRow>
                            <CancelButton
                                disabled={isSaving}
                                onCancel={async () => setIsEditing(false)}
                            />
                            <SaveButton
                                disabled={isSaving}
                                onSave={handleSubmit}
                            />
                        </FormButtonRow>
                    )}
                </Form>
            </Card.Body>
        </Card>
    );

    async function loadManagerRolesWithAuthorizations() {
        const url = `/api/ksyos/managerRolesWithAuthorizations?department=${encodeURIComponent(department)}`;
        const result: IManagerRoleWithAuthorization[] = (await abortableFetch.fetchJson(url, 'GET')).json?.items;
        const managerAuthorizationsGroupedByRole = groupByRole(result);
        setManagerAuthorizations(managerAuthorizationsGroupedByRole);
    }

    async function handleSubmit() {
        setIsSaving(true);
        const { item, errors } = validate(managerAuthorizationsSchema, managerAuthorizations);
        const url = `/api/ksyos/managerRolesWithAuthorizations`;
        if (errors.size === 0 && item) {
            await abortableFetch.postJson(url, item);
            setIsEditing(false);
        }
        setIsSaving(false);
    }

    function getValues(managerRoleAuthorizations: IManagerRoleWithAuthorization[]) {
        return managerRoleAuthorizations.map((managerRoleAuthorization) => ({
            value: managerRoleAuthorization.managerAuthorization.name,
            label: managerAuthorizationDescriptionTranslation[managerRoleAuthorization.managerAuthorization.name]
                .description,
        }));
    }

    function getOptions() {
        return Object.entries(managerAuthorizationDescriptionTranslation).map(
            ([authorization, { group, description }]) => ({
                value: authorization as ManagerAuthorization,
                label: description,
            })
        );
    }

    function handleChange(
        changedItem: MultiValue<{
            label: string;
            value: ManagerAuthorization;
        }>,
        role: string
    ) {
        const updatedManagerAuthorizations = {
            ...managerAuthorizations!,
            [role]: changedItem.map(({ value, label }) => ({
                name: role,
                managerAuthorization: { name: value },
            })),
        };
        setManagerAuthorizations(updatedManagerAuthorizations);
    }

    function getManagerRoleAuthorizationText(managerRoleAuthorizations: IManagerRoleWithAuthorization[]) {
        return managerRoleAuthorizations.length
            ? managerRoleAuthorizations
                  .map(
                      (managerRoleAuthorization) =>
                          managerAuthorizationDescriptionTranslation[managerRoleAuthorization.managerAuthorization.name]
                              .description
                  )
                  .join(', ')
            : '-';
    }

    function groupByRole(managerRolesWithAuthorization: IManagerRoleWithAuthorization[]) {
        const authorizationsGroupedByRole = managerRolesWithAuthorization.reduce(
            (groupedAuthorization, managerRoleAuthorization) => {
                if (groupedAuthorization[managerRoleAuthorization.name]) {
                    groupedAuthorization[managerRoleAuthorization.name].push(managerRoleAuthorization);
                } else {
                    groupedAuthorization[managerRoleAuthorization.name] = [];
                    if (managerRoleAuthorization.managerAuthorization.name) {
                        groupedAuthorization[managerRoleAuthorization.name].push(managerRoleAuthorization);
                    }
                }

                return groupedAuthorization;
            },
            {} as ManagerAuthorizationsGroupedByRole
        );

        return authorizationsGroupedByRole;
    }
}
